import React from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'

// ── Pure helpers ───────────────────────────────────────────────────────────────

function getCourtMapUrl(court) {
  const query = court.address_line1
    ? [court.address_line1, court.city, court.state, court.zip]
        .filter(Boolean)
        .join(', ')
    : [court.name, court.city, court.state].filter(Boolean).join(', ')
  return query
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    : null
}

function formatLevelContext(player) {
  if (player?.league_level_skill_level != null) {
    return `${Number(player.league_level_skill_level).toFixed(1)}`
  }
  return player?.skill_level || '—'
}

// Returns className + style for the outer draggable container.
// cursor:not-allowed is applied to the whole card when disabled for visual
// clarity; when enabled the grab cursor lives on the drag handle only.
function draggableContainerProps(isDragging, disabled) {
  return {
    className: `mb-2 p-2 bg-light border${isDragging ? ' opacity-50' : ''}${disabled ? ' disabled' : ''}`,
    style: {
      opacity: disabled ? 0.6 : 1,
      ...(disabled ? { cursor: 'not-allowed' } : {}),
    },
  }
}

// ── Shared presentational pieces ──────────────────────────────────────────────

// Drag handle — the only element that receives dnd-kit listeners.
// Scoping listeners here means interactive elements elsewhere in the card
// (links, buttons) no longer need event suppression to avoid drag starts.
function DragHandle({ listeners, attributes, disabled }) {
  return (
    <span
      {...(!disabled ? listeners : {})}
      {...attributes}
      className="me-2 text-muted flex-shrink-0 align-self-center"
      style={{ cursor: disabled ? 'not-allowed' : 'grab' }}
      aria-label="Drag to reorder"
    >
      <i className="bi bi-grip-vertical"></i>
    </span>
  )
}

// Renders a court name as a clickable Google Maps link (or plain text) plus
// optional city. React escapes JSX text and href attributes automatically.
// home_court is null when a player skipped court selection or the court was
// deleted after registration (backend returns null for missing court IDs).
function CourtDisplay({ court }) {
  if (!court) {
    return <span className="text-muted small">— No court selected</span>
  }
  const mapUrl = getCourtMapUrl(court)
  return (
    <span className="text-muted small">
      <i className="fas fa-map-marker-alt me-1"></i>
      {mapUrl && court.name ? (
        <a
          href={mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${court.name} in Google Maps`}
        >
          {court.name}
          <i
            className="bi bi-box-arrow-up-right ms-1"
            aria-hidden="true"
            style={{ fontSize: '0.75em' }}
          ></i>
        </a>
      ) : (
        court.name
      )}
      {court.city ? `, ${court.city}` : ''}
    </span>
  )
}

// Renders the partner row inside a doubles player card.
function PartnerDisplay({ player, onDecouplePartner }) {
  if (!player.partner) {
    return (
      <span className="text-warning small">
        <i className="fas fa-user me-1"></i>
        No partner
      </span>
    )
  }
  return (
    <span className="small">
      <i className="fas fa-user-friends me-1 text-success"></i>
      Partner: <strong>{player.partner.partner_name}</strong>
      {onDecouplePartner && (
        <button
          className="btn btn-link btn-sm text-danger p-0 ms-2"
          onClick={(e) => {
            e.stopPropagation()
            onDecouplePartner(player)
          }}
          title="Decouple partner"
          aria-label={`Decouple ${player.first_name} ${player.last_name} from partner`}
          data-testid="decouple-partner-player-btn"
          data-track="admin.decouple_partner"
          style={{ cursor: 'pointer' }}
        >
          <i className="fas fa-unlink"></i>
        </button>
      )}
    </span>
  )
}

// ── Exported components ────────────────────────────────────────────────────────

export function DraggablePlayer({
  player,
  disabled = false,
  canRemove = false,
  onRemove = null,
  isDoubles = false,
  onDecouplePartner = null,
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: player.id,
      disabled,
    })

  const containerProps = draggableContainerProps(isDragging, disabled)

  return (
    <div
      ref={setNodeRef}
      className={containerProps.className}
      style={{
        ...containerProps.style,
        transform: CSS.Translate.toString(transform),
      }}
    >
      <div className="d-flex justify-content-between align-items-start">
        <DragHandle
          listeners={listeners}
          attributes={attributes}
          disabled={disabled}
        />
        <div className="d-flex flex-column flex-grow-1">
          <div>
            <strong>
              {player.first_name} {player.last_name}
            </strong>{' '}
            ({formatLevelContext(player)})
            {disabled && <i className="fas fa-lock ms-2 text-muted"></i>}
          </div>
          <div className="mt-1">
            <CourtDisplay court={player.home_court} />
          </div>
          {isDoubles && (
            <div className="mt-1">
              <PartnerDisplay
                player={player}
                onDecouplePartner={onDecouplePartner}
              />
            </div>
          )}
        </div>
        {canRemove && onRemove && (
          <button
            className="btn btn-sm btn-danger ms-2"
            onClick={(e) => {
              e.stopPropagation()
              onRemove(player)
            }}
            title="Remove from league"
            data-track="admin.remove_player_from_league"
            style={{ cursor: 'pointer' }}
          >
            <i className="fas fa-trash"></i>
          </button>
        )}
      </div>
    </div>
  )
}

export function DraggableTeam({
  teamId,
  primaryPlayerId,
  players: teamPlayers,
  disabled = false,
  onDecouplePartner = null,
}) {
  const isIncomplete = !teamPlayers || teamPlayers.length < 2

  // Use player-scoped ID so the same team in two divisions has unique drag IDs
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `team-${teamId}-${primaryPlayerId}`,
      disabled: disabled || isIncomplete,
    })

  // Guard: fall back to individual player card if team is incomplete
  if (isIncomplete) {
    return teamPlayers?.[0] ? (
      <DraggablePlayer player={teamPlayers[0]} disabled={disabled} isDoubles />
    ) : null
  }

  const teamCourt = teamPlayers[0]?.home_court || teamPlayers[1]?.home_court
  const containerProps = draggableContainerProps(isDragging, disabled)

  return (
    <div
      ref={setNodeRef}
      className={containerProps.className}
      style={{
        ...containerProps.style,
        transform: CSS.Translate.toString(transform),
      }}
    >
      <div className="d-flex justify-content-between align-items-start">
        <DragHandle
          listeners={listeners}
          attributes={attributes}
          disabled={disabled}
        />
        <div className="d-flex flex-column flex-grow-1">
          <div>
            <i className="fas fa-user-friends me-1 text-success"></i>
            <strong>
              {teamPlayers[0].first_name} {teamPlayers[0].last_name}
            </strong>{' '}
            ({formatLevelContext(teamPlayers[0])}){' & '}
            <strong>
              {teamPlayers[1].first_name} {teamPlayers[1].last_name}
            </strong>{' '}
            ({formatLevelContext(teamPlayers[1])})
            {disabled && <i className="fas fa-lock ms-2 text-muted"></i>}
          </div>
          <div className="mt-1">
            <CourtDisplay court={teamCourt} />
          </div>
        </div>
        {onDecouplePartner && (
          <button
            className="btn btn-sm btn-outline-danger ms-2"
            onClick={(e) => {
              e.stopPropagation()
              onDecouplePartner(teamPlayers[0])
            }}
            title="Decouple partners"
            aria-label={`Decouple team: ${teamPlayers[0].first_name} ${teamPlayers[0].last_name} and ${teamPlayers[1].first_name} ${teamPlayers[1].last_name}`}
            data-testid="decouple-partner-team-btn"
            data-track="admin.decouple_partner"
            style={{ cursor: 'pointer' }}
          >
            <i className="fas fa-unlink"></i>
          </button>
        )}
      </div>
    </div>
  )
}

export function DroppableDivision({
  id,
  label,
  children,
  canDelete,
  onDelete,
  disabled = false,
  canEdit = false,
  onEditName = null,
}) {
  const { setNodeRef, isOver } = useDroppable({ id, disabled })

  const [isEditing, setIsEditing] = React.useState(false)
  const [editedName, setEditedName] = React.useState(label)

  const handleEditClick = () => {
    setEditedName(label)
    setIsEditing(true)
  }

  const handleSave = () => {
    if (editedName.trim() && editedName !== label && onEditName) {
      onEditName(editedName.trim())
    }
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditedName(label)
    setIsEditing(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave()
    else if (e.key === 'Escape') handleCancel()
  }

  return (
    <div className="col-12 col-md-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        {isEditing ? (
          <div className="d-flex align-items-center flex-grow-1">
            <input
              type="text"
              className="form-control form-control-sm me-2"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              placeholder="Division Name"
            />
            <button
              className="btn btn-sm btn-success me-1"
              onClick={handleSave}
              title="Save"
            >
              <i className="fas fa-check"></i> Save
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={handleCancel}
              title="Cancel"
            >
              <i className="fas fa-times"></i> Cancel
            </button>
          </div>
        ) : (
          <>
            <h5 className="mb-0">
              {label}
              {canEdit && onEditName && (
                <button
                  className="btn btn-sm btn-outline-secondary ms-2"
                  onClick={handleEditClick}
                  title="Edit division name"
                  data-track="admin.edit_division_name"
                >
                  <i className="fas fa-pen"></i> Edit
                </button>
              )}
            </h5>
            {canDelete && onDelete && (
              <button
                className="btn btn-sm btn-danger"
                onClick={onDelete}
                title="Delete division"
              >
                <i className="fas fa-trash"></i>
              </button>
            )}
          </>
        )}
      </div>
      <div
        ref={setNodeRef}
        className={`border p-3 min-vh-25${isOver && !disabled ? ' bg-info' : ''}${disabled ? ' disabled' : ''}`}
        data-testid={`droppable-division-${id}`}
        style={{
          minHeight: '100px',
          opacity: disabled ? 0.6 : 1,
          backgroundColor: disabled ? '#f8f9fa' : undefined,
        }}
      >
        {children}
      </div>
    </div>
  )
}
